import React, { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  Button,
  IconButton,
  Toolbar,
  Typography,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel
} from '@material-ui/core'
import SaveIcon from '@material-ui/icons/Save'
import DeleteIcon from '@material-ui/icons/Delete'

const MAPPING_TYPES = [
  { value: 'direct', label: 'Direct' },
  { value: 'composite', label: 'Composite' },
  { value: 'shared_allocated', label: 'Shared Allocated' },
  { value: 'ignore', label: 'Ignore' }
]

const STATUS_CHIP_STYLES = {
  mapped: { backgroundColor: '#c8e6c9', color: '#2e7d32', label: 'Mapped' },
  unmapped: { backgroundColor: '#ffcdd2', color: '#c62828', label: 'Unmapped' },
  ignored: { backgroundColor: '#eeeeee', color: '#616161', label: 'Ignored' },
  partial: { backgroundColor: '#fff9c4', color: '#f57f17', label: 'Partial' }
}

const defaultRowState = () => ({
  serviceCategory: '',
  serviceType: '',
  mappingType: 'direct',
  allocationPercent: ''
})

const MappingTab = ({ packageId, url, onMappingUpdate }) => {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  // Per-row edit state: { [itemId]: { serviceCategory, serviceType, mappingType, allocationPercent } }
  const [rowState, setRowState] = useState({})
  // Per-row saving state
  const [saving, setSaving] = useState({})
  // Per-row deleting state
  const [deleting, setDeleting] = useState({})
  // Selected rows for bulk assign
  const [selected, setSelected] = useState({})
  // Bulk assign field state
  const [bulkState, setBulkState] = useState(defaultRowState())
  const [bulkApplying, setBulkApplying] = useState(false)
  // Autocomplete hints: unique serviceCategory values seen in this package
  const [categoryHints, setCategoryHints] = useState([])

  const fetchItems = useCallback(() => {
    if (!packageId) return
    setLoading(true)
    setError('')
    axios.get(`${url}/api/bid-estimation/bid-packages/${packageId}/items`)
      .then(res => {
        const data = res.data || []
        setItems(data)

        // Seed row state from existing mappings
        const initialRowState = {}
        const categories = new Set()
        data.forEach(item => {
          const m = item.mapping || {}
          initialRowState[item._id] = {
            serviceCategory: m.serviceCategory || '',
            serviceType: m.serviceType || '',
            mappingType: m.mappingType || 'direct',
            allocationPercent: m.allocationPercent != null ? String(m.allocationPercent) : ''
          }
          if (m.serviceCategory) categories.add(m.serviceCategory)
        })
        setRowState(initialRowState)
        setCategoryHints([...categories])
        setLoading(false)
      })
      .catch(err => {
        setError(err.response?.data?.message || 'Failed to load items.')
        setLoading(false)
      })
  }, [packageId, url])

  useEffect(() => {
    fetchItems()
  }, [fetchItems])

  const updateRowField = (itemId, field, value) => {
    setRowState(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], [field]: value }
    }))
  }

  const handleSave = async (item) => {
    const state = rowState[item._id] || defaultRowState()
    if (!state.serviceCategory.trim() || !state.serviceType.trim()) return

    setSaving(prev => ({ ...prev, [item._id]: true }))
    try {
      const payload = {
        serviceCategory: state.serviceCategory.trim(),
        serviceType: state.serviceType.trim(),
        mappingType: state.mappingType,
        ...(state.mappingType === 'shared_allocated' && state.allocationPercent !== ''
          ? { allocationPercent: parseFloat(state.allocationPercent) }
          : {})
      }

      if (item.mappingId) {
        await axios.put(`${url}/api/bid-estimation/bid-item-mappings/${item.mappingId}`, payload)
      } else {
        await axios.post(`${url}/api/bid-estimation/bid-items/${item._id}/mappings`, payload)
      }

      // Update category hints
      if (payload.serviceCategory && !categoryHints.includes(payload.serviceCategory)) {
        setCategoryHints(prev => [...prev, payload.serviceCategory])
      }

      fetchItems()
      if (onMappingUpdate) onMappingUpdate()
    } catch (err) {
      console.error('Failed to save mapping:', err)
    } finally {
      setSaving(prev => ({ ...prev, [item._id]: false }))
    }
  }

  const handleDelete = async (item) => {
    if (!item.mappingId) return
    setDeleting(prev => ({ ...prev, [item._id]: true }))
    try {
      await axios.delete(`${url}/api/bid-estimation/bid-item-mappings/${item.mappingId}`)
      fetchItems()
      if (onMappingUpdate) onMappingUpdate()
    } catch (err) {
      console.error('Failed to delete mapping:', err)
    } finally {
      setDeleting(prev => ({ ...prev, [item._id]: false }))
    }
  }

  const handleSelectAll = (checked) => {
    if (checked) {
      const all = {}
      items.forEach(item => { all[item._id] = true })
      setSelected(all)
    } else {
      setSelected({})
    }
  }

  const handleSelectRow = (itemId, checked) => {
    setSelected(prev => ({ ...prev, [itemId]: checked }))
  }

  const selectedIds = Object.keys(selected).filter(id => selected[id])
  const allSelected = items.length > 0 && selectedIds.length === items.length
  const someSelected = selectedIds.length > 0 && !allSelected

  const handleBulkApply = async () => {
    if (!bulkState.serviceCategory.trim() || !bulkState.serviceType.trim()) return
    if (selectedIds.length === 0) return

    setBulkApplying(true)
    const payload = {
      serviceCategory: bulkState.serviceCategory.trim(),
      serviceType: bulkState.serviceType.trim(),
      mappingType: bulkState.mappingType,
      ...(bulkState.mappingType === 'shared_allocated' && bulkState.allocationPercent !== ''
        ? { allocationPercent: parseFloat(bulkState.allocationPercent) }
        : {})
    }

    try {
      await Promise.all(selectedIds.map(itemId =>
        axios.post(`${url}/api/bid-estimation/bid-items/${itemId}/mappings`, payload)
      ))
      setSelected({})
      setBulkState(defaultRowState())
      fetchItems()
      if (onMappingUpdate) onMappingUpdate()
    } catch (err) {
      console.error('Bulk assign failed:', err)
    } finally {
      setBulkApplying(false)
    }
  }

  const getMappingStatusChip = (item) => {
    const status = item.mappingStatus || 'unmapped'
    const cfg = STATUS_CHIP_STYLES[status] || STATUS_CHIP_STYLES.unmapped
    return (
      <Chip
        size="small"
        label={cfg.label}
        style={{ backgroundColor: cfg.backgroundColor, color: cfg.color, fontSize: 11 }}
      />
    )
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
        <CircularProgress />
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ padding: 16 }}>
        <Typography color="error">{error}</Typography>
      </div>
    )
  }

  return (
    <div>
      {/* Bulk assign toolbar — visible when any rows are selected */}
      {selectedIds.length > 0 && (
        <Paper style={{ marginBottom: 12, backgroundColor: '#e3f2fd' }}>
          <Toolbar style={{ gap: 12, flexWrap: 'wrap', minHeight: 64 }}>
            <Typography variant="subtitle2" style={{ marginRight: 8, whiteSpace: 'nowrap' }}>
              {selectedIds.length} selected
            </Typography>
            <TextField
              label="Service Category"
              value={bulkState.serviceCategory}
              onChange={e => setBulkState(prev => ({ ...prev, serviceCategory: e.target.value }))}
              variant="outlined"
              size="small"
              style={{ minWidth: 160 }}
              inputProps={{ list: 'bulk-category-hints' }}
            />
            <datalist id="bulk-category-hints">
              {categoryHints.map(h => <option key={h} value={h} />)}
            </datalist>
            <TextField
              label="Service Type"
              value={bulkState.serviceType}
              onChange={e => setBulkState(prev => ({ ...prev, serviceType: e.target.value }))}
              variant="outlined"
              size="small"
              style={{ minWidth: 160 }}
            />
            <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
              <InputLabel>Mapping Type</InputLabel>
              <Select
                value={bulkState.mappingType}
                onChange={e => setBulkState(prev => ({ ...prev, mappingType: e.target.value }))}
                label="Mapping Type"
              >
                {MAPPING_TYPES.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {bulkState.mappingType === 'shared_allocated' && (
              <TextField
                label="Allocation %"
                value={bulkState.allocationPercent}
                onChange={e => setBulkState(prev => ({ ...prev, allocationPercent: e.target.value }))}
                variant="outlined"
                size="small"
                type="number"
                style={{ width: 110 }}
                inputProps={{ min: 0, max: 100, step: 0.1 }}
              />
            )}
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleBulkApply}
              disabled={bulkApplying || !bulkState.serviceCategory.trim() || !bulkState.serviceType.trim()}
            >
              {bulkApplying ? <CircularProgress size={18} /> : 'Apply to Selected'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setSelected({})}
            >
              Clear Selection
            </Button>
          </Toolbar>
        </Paper>
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow style={{ backgroundColor: '#f5f5f5' }}>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={someSelected}
                  checked={allSelected}
                  onChange={e => handleSelectAll(e.target.checked)}
                  size="small"
                />
              </TableCell>
              <TableCell style={{ minWidth: 220 }}>Description</TableCell>
              <TableCell style={{ width: 100 }}>Status</TableCell>
              <TableCell style={{ minWidth: 160 }}>Service Category</TableCell>
              <TableCell style={{ minWidth: 160 }}>Service Type</TableCell>
              <TableCell style={{ minWidth: 150 }}>Mapping Type</TableCell>
              <TableCell style={{ width: 100 }}>Alloc %</TableCell>
              <TableCell style={{ width: 80 }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" style={{ padding: 20, color: '#757575' }}>
                    No items found for this bid package.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map(item => {
                const rs = rowState[item._id] || defaultRowState()
                const isSaving = saving[item._id]
                const isDeleting = deleting[item._id]
                const isSelected = !!selected[item._id]
                const canSave = rs.serviceCategory.trim() && rs.serviceType.trim()

                return (
                  <TableRow
                    key={item._id}
                    selected={isSelected}
                    style={{ backgroundColor: isSelected ? '#e8f5e9' : undefined }}
                  >
                    {/* Checkbox */}
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        onChange={e => handleSelectRow(item._id, e.target.checked)}
                        size="small"
                      />
                    </TableCell>

                    {/* Description (read-only) */}
                    <TableCell>
                      <Typography variant="body2" style={{ fontSize: 13 }}>
                        {item.description || item.rawDescription || '—'}
                      </Typography>
                      {item.section && (
                        <Typography variant="caption" color="textSecondary">
                          {item.section}
                        </Typography>
                      )}
                    </TableCell>

                    {/* Mapping status */}
                    <TableCell>{getMappingStatusChip(item)}</TableCell>

                    {/* Service Category */}
                    <TableCell>
                      <TextField
                        value={rs.serviceCategory}
                        onChange={e => updateRowField(item._id, 'serviceCategory', e.target.value)}
                        variant="outlined"
                        size="small"
                        fullWidth
                        placeholder="e.g. Irrigation"
                        inputProps={{ list: `cat-hints-${item._id}` }}
                      />
                      <datalist id={`cat-hints-${item._id}`}>
                        {categoryHints.map(h => <option key={h} value={h} />)}
                      </datalist>
                    </TableCell>

                    {/* Service Type */}
                    <TableCell>
                      <TextField
                        value={rs.serviceType}
                        onChange={e => updateRowField(item._id, 'serviceType', e.target.value)}
                        variant="outlined"
                        size="small"
                        fullWidth
                        placeholder="e.g. Monthly"
                      />
                    </TableCell>

                    {/* Mapping Type */}
                    <TableCell>
                      <FormControl variant="outlined" size="small" fullWidth>
                        <Select
                          value={rs.mappingType}
                          onChange={e => updateRowField(item._id, 'mappingType', e.target.value)}
                        >
                          {MAPPING_TYPES.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>

                    {/* Allocation Percent (conditional) */}
                    <TableCell>
                      {rs.mappingType === 'shared_allocated' ? (
                        <TextField
                          value={rs.allocationPercent}
                          onChange={e => updateRowField(item._id, 'allocationPercent', e.target.value)}
                          variant="outlined"
                          size="small"
                          type="number"
                          fullWidth
                          inputProps={{ min: 0, max: 100, step: 0.1 }}
                          placeholder="0–100"
                        />
                      ) : (
                        <Typography variant="body2" color="textSecondary" style={{ fontSize: 12 }}>—</Typography>
                      )}
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleSave(item)}
                        disabled={isSaving || !canSave}
                        title="Save mapping"
                      >
                        {isSaving ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
                      </IconButton>
                      {item.mappingId && (
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => handleDelete(item)}
                          disabled={isDeleting}
                          title="Delete mapping"
                        >
                          {isDeleting ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  )
}

export default MappingTab
